import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import { useAuthStore } from './store/authStore';
import { trackPageView } from './analytics';

// Layouts
import MainLayout from './layouts/MainLayout';
import AuthLayout from './layouts/AuthLayout';

// Public Pages
import Landing from './pages/Landing';

// Auth Pages
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';
import VerifyEmail from './pages/auth/VerifyEmail';
import SetPassword from './pages/auth/SetPassword';
import ForgotPassword from './pages/auth/ForgotPassword';

// Legal Pages
import TermsOfService from './pages/legal/TermsOfService';
import PrivacyPolicy from './pages/legal/PrivacyPolicy';

// Dashboard Pages
import DashboardList from './pages/dashboard/DashboardList';
import DashboardBuilder from './pages/dashboard/DashboardBuilder';

// Data Pages
import DataSourceList from './pages/data/DataSourceList';
import DataSourceDetail from './pages/data/DataSourceDetail';
import DataUpload from './pages/data/DataUpload';
import DataSourceCreate from './pages/data/DataSourceCreate';
import RelationshipEditor from './pages/data/RelationshipEditor';
// AI Pages
import AIChat from './pages/ai/AIChat';

// Admin Pages
import UserManagement from './pages/admin/UserManagement';
import CompanySettings from './pages/admin/CompanySettings';
import BillingUsage from './pages/admin/BillingUsage';
import Companies from './pages/admin/Companies';
import WarehouseStorage from './pages/admin/WarehouseStorage';
import SDKIntegration from './pages/admin/SDKIntegration';

// Settings Pages
import UserSettings from './pages/settings/UserSettings';

// Help & Tickets
import Help from './pages/Help';
import MyTickets from './pages/tickets/MyTickets';
import TicketDetail from './pages/tickets/TicketDetail';

// Referrals
import ReferralDashboard from './pages/referrals/ReferralDashboard';

// Pricing Page
import Pricing from './pages/Pricing';

// Public Dashboard (shared link, no auth)
import PublicDashboard from './pages/public/PublicDashboard';

// Documentation Page
import Documentation from './pages/Documentation';

// Protected Route wrapper
function ProtectedRoute({ children }) {
  const { isAuthenticated, isLoading, _hasHydrated } = useAuthStore();
  
  // Wait for hydration and loading to complete before making auth decisions
  if (!_hasHydrated || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface-50 dark:bg-surface-950">
        <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
}

// Home Route Component (redirects based on auth status)
function HomeRoute() {
  const { isAuthenticated, isLoading, _hasHydrated } = useAuthStore();
  
  // Wait for hydration and loading to complete
  if (!_hasHydrated || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface-50 dark:bg-surface-950">
        <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  // Redirect authenticated users to dashboard
  if (isAuthenticated) {
    return <Navigate to="/dashboards" replace />;
  }
  
  // Show landing page to non-authenticated users
  return <Landing />;
}

function RouteTracker() {
  const location = useLocation();
  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location]);
  return null;
}

function App() {
  return (
    <>
      <RouteTracker />
      <Routes>
      {/* Home Page - Shows landing for public, redirects to dashboard for authenticated */}
      <Route path="/" element={<HomeRoute />} />
      
      {/* Public Pages (no layout) */}
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/docs" element={<Documentation />} />
      <Route path="/public/dashboard/:slug" element={<PublicDashboard />} />
      
      {/* Legal Pages (public, no layout) */}
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      
      {/* Auth Routes */}
      <Route element={<AuthLayout />}>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/auth/verify" element={<VerifyEmail />} />
        <Route path="/auth/set-password" element={<SetPassword />} />
        <Route path="/auth/forgot-password" element={<ForgotPassword />} />
        <Route path="/auth/reset-password" element={<SetPassword isReset />} />
      </Route>
      
      {/* Protected Routes */}
      <Route element={
        <ProtectedRoute>
          <MainLayout />
        </ProtectedRoute>
      }>
        {/* Dashboards */}
        <Route path="/dashboards" element={<DashboardList />} />
        <Route path="/dashboards/:id" element={<DashboardBuilder />} />
        <Route path="/dashboards/new" element={<DashboardBuilder />} />
        
        {/* Data Sources */}
        <Route path="/data" element={<DataSourceList />} />
        <Route path="/data/new" element={<DataSourceCreate />} />
        <Route path="/data/upload" element={<DataUpload />} />
        <Route path="/data/:id" element={<DataSourceDetail />} />
        
        {/* Data Studio (Semantic Layer) - Combined into RelationshipEditor */}
        <Route path="/data/studio" element={<Navigate to="/data/relationships" replace />} />
        <Route path="/data/relationships" element={<RelationshipEditor />} />
        
        {/* AI Chat */}
        <Route path="/ai" element={<AIChat />} />
        <Route path="/ai/:conversationId" element={<AIChat />} />
        
        {/* Admin */}
        <Route path="/admin/companies" element={<Companies />} />
        <Route path="/admin/users" element={<UserManagement />} />
        <Route path="/admin/settings" element={<CompanySettings />} />
        <Route path="/admin/billing" element={<BillingUsage />} />
        <Route path="/admin/warehouse" element={<WarehouseStorage />} />
        <Route path="/admin/sdk" element={<SDKIntegration />} />
        
        {/* User Settings */}
        <Route path="/settings" element={<UserSettings />} />
        
        {/* Help & Tickets */}
        <Route path="/help" element={<Help />} />
        <Route path="/tickets" element={<MyTickets />} />
        <Route path="/tickets/:id" element={<TicketDetail />} />
        
        {/* Referrals */}
        <Route path="/referrals" element={<ReferralDashboard />} />
      </Route>
      
      {/* Catch all */}
      <Route path="*" element={<Navigate to="/dashboards" replace />} />
    </Routes>
    </>
  );
}

export default App;
